import { Card } from '../ui/Card.jsx';
import { Button } from '../ui/Button.jsx';
import { WorkerBadge } from '../ui/WorkerBadge.jsx';
import { TaskSelectModal } from './TaskSelectModal.jsx';
import { useState, useRef, useEffect } from 'react';
import { BUILDINGS } from '../../config/buildings.js';
import { CONFIG } from '../../config/constants.js';
import { useAudio } from '../../hooks/useAudio.js';
import { TASK_DEFINITIONS } from '../../config/tasks.js';

export function AssignCard({ workers, assignments, tempWorker, onAssign, onRoll, buildings, buildingInProgress }) {
  const audio = useAudio();
  const [selectedWorker, setSelectedWorker] = useState(null);
  const lastFocusedButtonRef = useRef(null);

  // Return focus to the button that opened the modal when it closes
  useEffect(() => {
    if (!selectedWorker && lastFocusedButtonRef.current) {
      lastFocusedButtonRef.current.focus();
      lastFocusedButtonRef.current = null;
    }
  }, [selectedWorker]);

  // Immature children cannot work — exclude from the assignment UI (they still eat)
  const aliveWorkers = workers.filter(w => w.status !== 'dead' && w.status !== 'immature');
  const allWorkers = tempWorker
    ? [...aliveWorkers, { id: 'temp', status: 'healthy', isTemp: true }]
    : aliveWorkers;


  // Calculate building progress data for display
  const buildingData = buildingInProgress ? {
    name: BUILDINGS[buildingInProgress.type]?.name || buildingInProgress.type,
    progress: buildingInProgress.progress || 0,
    threshold: BUILDINGS[buildingInProgress.type]?.buildThreshold || CONFIG.BUILD_THRESHOLD,
    percentage: Math.min(100, ((buildingInProgress.progress || 0) /
      (BUILDINGS[buildingInProgress.type]?.buildThreshold || CONFIG.BUILD_THRESHOLD)) * 100)
  } : null;

  // Generate task labels dynamically from task definitions
  const taskLabels = Object.fromEntries(
    Object.values(TASK_DEFINITIONS).map(task => [task.id, task.label])
  );

  const handleSelectTask = (workerId, task) => {
    setSelectedWorker(null);
    onAssign(workerId, task);
  };

  const handleOpenModal = (worker, buttonElement) => {
    lastFocusedButtonRef.current = buttonElement;
    setSelectedWorker(worker);
  };

  return (
    <>
      <Card title="Assign Workers" icon="📋">
        {buildingData && (
          <div
            className="mb-4 p-3 bg-amber-100 border-2 border-amber-600 rounded-lg"
            role="region"
            aria-label="Building in progress"
          >
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold text-amber-900">
                🔨 Building: {buildingData.name}
              </span>
              <span className="font-bold text-amber-700">
                {buildingData.progress}/{buildingData.threshold}
              </span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-amber-200 rounded-full h-3 mb-2">
              <div
                className="bg-amber-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${buildingData.percentage}%` }}
                role="progressbar"
                aria-valuenow={buildingData.progress}
                aria-valuemin="0"
                aria-valuemax={buildingData.threshold}
                aria-label={`Building progress: ${buildingData.progress} out of ${buildingData.threshold}`}
              />
            </div>

            {/* Helper Text */}
            <div className="text-xs text-amber-700">
              Workers assigned to Build will contribute to this project
            </div>
          </div>
        )}

        <div className="space-y-3 mb-6" role="list" aria-label="Worker assignments">
          {allWorkers.map(worker => {
            const currentTask = assignments[worker.id] || (worker.status === 'injured' ? 'rest' : 'farm');

            return (
              <div key={worker.id} className="flex items-center gap-3" role="listitem">
                <WorkerBadge worker={worker} />
                {worker.isTemp && <span className="text-xs text-amber-600">(temp)</span>}
                <div className="flex-1 p-2 bg-amber-100 rounded-lg">
                  <span className="text-amber-900 font-medium">{taskLabels[currentTask]}</span>
                </div>
                <button
                  onClick={(e) => {
                    audio.playSound('ui_confirm');
                    handleOpenModal(worker, e.currentTarget);
                  }}
                  className="px-3 py-2 bg-amber-700 hover:bg-amber-800 text-amber-50 border-2 border-amber-900 rounded font-medium transition-colors focus:ring-2 focus:ring-amber-400 focus:outline-none"
                  aria-label={`Change assignment for worker ${worker.id}`}
                >
                  Assign
                </button>
              </div>
            );
          })}
        </div>
        <div className="text-center">
          <Button onClick={onRoll}>🎲 Roll Dice</Button>
        </div>
      </Card>

      {selectedWorker && (
        <TaskSelectModal
          worker={selectedWorker}
          onSelectTask={(task) => handleSelectTask(selectedWorker.id, task)}
          onClose={() => setSelectedWorker(null)}
          gameState={{ buildings, buildingInProgress }}
        />
      )}
    </>
  );
}
